<template>
  <header
    :class="['sc-header', { 'is-transparent': isTransparent, 'is-scrolled': scrolled }]"
    @mouseenter="hovered = true"
    @mouseleave="hovered = false"
  >
    <div class="sc-header__inner sc-container">
      <router-link to="/" class="sc-header__logo" @click.native="closeMenu">
        <img
          src="@/assets/images/logo.png"
          alt="苏成能源科技 Sucheng Energy Technology"
          class="sc-header__logo-img"
        />
      </router-link>

      <nav :class="['sc-header__nav', { 'is-open': menuOpen }]">
        <router-link
          v-for="item in navItems"
          :key="item.path"
          :to="item.path"
          class="sc-header__link"
          active-class="is-active"
          @click.native="closeMenu"
        >
          {{ item.label }}
        </router-link>
      </nav>

      <div class="sc-header__actions">
        <router-link to="/contact" class="sc-header__cta" @click.native="closeMenu">
          免费咨询
        </router-link>
        <button
          class="sc-header__toggle"
          :class="{ 'is-open': menuOpen }"
          aria-label="切换菜单"
          @click="menuOpen = !menuOpen"
        >
          <span />
          <span />
          <span />
        </button>
      </div>
    </div>
    <div v-if="menuOpen" class="sc-header__overlay" @click="closeMenu" />
  </header>
</template>

<script>
export default {
  name: 'AppHeader',
  data() {
    return {
      scrolled: false,
      hovered: false,
      menuOpen: false,
      navItems: [
        { label: '首页', path: '/' },
        { label: '产品中心', path: '/products' },
        { label: '解决方案', path: '/solutions' },
        { label: '合作伙伴', path: '/partners' },
        { label: '关于我们', path: '/about' },
        { label: '联系我们', path: '/contact' },
      ],
    }
  },
  computed: {
    isHomePage() {
      return this.$route.path === '/'
    },
    isTransparent() {
      return this.isHomePage && !this.scrolled && !this.hovered && !this.menuOpen
    },
  },
  watch: {
    $route() {
      this.menuOpen = false
      this.checkScroll()
    },
  },
  mounted() {
    window.addEventListener('scroll', this.onScroll, { passive: true })
    this.checkScroll()
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.onScroll)
  },
  methods: {
    onScroll() {
      this.checkScroll()
    },
    checkScroll() {
      this.scrolled = window.scrollY > 10
    },
    closeMenu() {
      this.menuOpen = false
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/styles/variables.scss';

.sc-header {
  --header-bg: #{$color-white};
  --link-color: #{$color-text};
  --link-active: #{$color-secondary};
  --toggle-color: #{$color-text};

  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  height: $header-height;
  background: var(--header-bg);
  transition: background 0.25s ease, box-shadow 0.25s ease;

  &.is-scrolled {
    box-shadow: $shadow-nav;
  }

  &.is-transparent {
    --header-bg: transparent;
    --link-color: rgba(255, 255, 255, 0.92);
    --link-active: #{$color-secondary};
    --toggle-color: #{$color-white};
    box-shadow: none;

    &:hover {
      --header-bg: #{$color-white};
      --link-color: #{$color-text};
      --link-active: #{$color-secondary};
      --toggle-color: #{$color-text};
      box-shadow: $shadow-nav;
    }
  }

  &__inner {
    display: flex;
    align-items: center;
    height: 100%;
    gap: 32px;
  }

  &__logo {
    display: flex;
    align-items: center;
    flex-shrink: 0;
  }

  &__logo-img {
    height: 56px;
    width: auto;
    display: block;
  }

  &__nav {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    justify-content: center;
  }

  &__link {
    padding: 8px 16px;
    font-size: 15px;
    color: var(--link-color);
    white-space: nowrap;
    border-radius: 4px;
    transition: color 0.25s ease;

    &:hover {
      color: var(--link-active);
    }

    &.is-active {
      color: var(--link-active);
      font-weight: 600;
    }
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
  }

  &__cta {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 36px;
    padding: 0 24px;
    background: $color-secondary;
    color: $color-white;
    font-size: 14px;
    font-weight: 500;
    border-radius: 20px;
    white-space: nowrap;
    transition: background 0.15s ease, transform 0.15s ease;

    &:hover {
      background: $color-secondary-dark;
      transform: translateY(-1px);
    }
  }

  &__toggle {
    display: none;
    flex-direction: column;
    justify-content: center;
    gap: 5px;
    width: 32px;
    height: 32px;
    padding: 4px;

    span {
      display: block;
      width: 100%;
      height: 2px;
      background: var(--toggle-color);
      border-radius: 1px;
      transition: transform 0.2s ease, opacity 0.2s ease, background 0.25s ease;
    }

    &.is-open {
      span:nth-child(1) {
        transform: translateY(7px) rotate(45deg);
      }
      span:nth-child(2) {
        opacity: 0;
      }
      span:nth-child(3) {
        transform: translateY(-7px) rotate(-45deg);
      }
    }
  }

  &__overlay {
    display: none;
  }
}

@media (max-width: $breakpoint-lg) {
  .sc-header {
    &__nav {
      position: fixed;
      top: $header-height;
      left: 0;
      right: 0;
      bottom: 0;
      flex-direction: column;
      justify-content: flex-start;
      padding: 24px;
      gap: 4px;
      background: $color-white;
      transform: translateX(100%);
      transition: transform 0.3s ease;

      &.is-open {
        transform: translateX(0);
      }
    }

    &__link {
      width: 100%;
      padding: 14px 16px;
      font-size: 16px;
      color: $color-text;

      &:hover,
      &.is-active {
        color: $color-secondary;
      }
    }

    &__toggle {
      display: flex;
    }

    &__overlay {
      display: block;
      position: fixed;
      inset: $header-height 0 0 0;
      background: rgba(0, 0, 0, 0.3);
      z-index: -1;
    }
  }
}

@media (max-width: $breakpoint-sm) {
  .sc-header {
    &__logo-img {
      height: 48px;
    }

    &__cta {
      padding: 0 16px;
      font-size: 13px;
    }
  }
}
</style>
